import { FC, useState, useRef, useEffect } from 'react';
import BordStatus from '../BordStatus/BordStatus';
import type { statusVariants } from '../BordStatus/BordStatus.type';
import './BordSelectStatus.scss';

interface IBordSelectStatusItem {
  label: string;
  value: string;
  variant?: statusVariants;
  disabled?: boolean;
}

/**
 * Props para el componente BordSelectStatus
 * @param items - Lista de items a mostrar en el dropdown
 * @param variant - Variante de estilo del status
 * @param value - Valor seleccionado actualmente
 * @param onChange - Función a ejecutar cuando se selecciona un nuevo valor
 * @param customClassName - Clase CSS personalizada
 * @param disableDropdown - Si es true, deshabilita completamente el dropdown
 */
interface IBordSelectStatusProps {
  items: Array<IBordSelectStatusItem>;
  variant?: statusVariants;
  value?: string;
  onChange?: (value: string) => void;
  customClassName?: string;
  customDropdownClassName?: string;
  disableDropdown?: boolean;
}

const BordSelectStatus: FC<IBordSelectStatusProps> = ({
  items,
  variant = 'default',
  value,
  onChange,
  customClassName,
  customDropdownClassName = 'w-32',
  disableDropdown = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const selectedItem = items?.find(item => item?.value === value) || items?.[0];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (selectedValue: string) => {
    onChange?.(selectedValue);
    setIsOpen(false);
  };

  return (
    <div className={`bordSelectStatus relative ${customClassName}`} ref={dropdownRef}>
      <div
        onClick={() => !disableDropdown && setIsOpen(!isOpen)}
        className={disableDropdown ? 'cursor-not-allowed' : 'cursor-pointer'}
      >
        <BordStatus
          label={selectedItem?.label}
          variant={selectedItem?.variant || variant}
          showArrow={!disableDropdown}
          isOpen={isOpen}
        />
      </div>
      {isOpen && !disableDropdown && (
        <div className={`bordSelectStatusDropdown ${customDropdownClassName}`}>
          {items
            .filter(item => item?.value !== selectedItem?.value)
            .map(item => (
              <div
                key={item?.value}
                className={`
                bordSelectStatusItem 
                ${item?.value === value ? 'selected' : ''} 
                ${item?.disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}
              `}
                onClick={() => !item?.disabled && handleSelect(item?.value)}
              >
                <span className="text-xss text-nudos-text-color">{item?.label}</span>
              </div>
            ))}
        </div>
      )}
    </div>
  );
};

export default BordSelectStatus;
